import React, { useEffect, useState } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Badge, Typography, Input, Button } from 'antd';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  ShopOutlined,
  UserOutlined,
  BarChartOutlined,
  AlertOutlined,
  SettingOutlined,
  LeftOutlined,
  RightOutlined,
  LogoutOutlined,
  UserSwitchOutlined,
  StarOutlined,
  FolderOutlined,
  SearchOutlined,
  FileSearchOutlined,
  MessageOutlined,
  ToolOutlined,
  CreditCardOutlined,
  PlusOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { useQuery } from 'react-query';
import api from '../config/api';
import NotificationBell from './NotificationBell';
import { WorkspaceTabBar } from './WorkspaceTabBar';
import { CreateTicketFromChatModal } from './chat/CreateTicketFromChatModal';
import { useAgentWorkspaceTabs } from '../contexts/AgentWorkspaceTabsContext';
import { useChronicBusinessAlerts } from '../hooks/useChronicBusinessAlerts';

const MOBILE_BREAKPOINT = 768;

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = () => setIsMobile(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

interface LayoutProps {
  children: React.ReactNode;
}

const LayoutComponent: React.FC<LayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const tabsContext = useAgentWorkspaceTabs();
  const { user, logout } = useAuth();
  const canManageReplacements = usePermission('manage_replacements');
  const canManageCreditCardServices = usePermission('manage_credit_card_services');
  const canAnalytics = usePermission('analytics_reports');
  const canFeedback = usePermission('feedback');
  const canAuditLogs = usePermission('audit_logs');
  const canTicketAudit = usePermission('ticket_audit');
  const canSalesPortalMgmt = usePermission('sales_agent_portal_management');
  const canAccessChat = usePermission('access_chat');
  const canCallMeQueue = usePermission('call_me_queue');
  const canRetainIQ = usePermission('retain_iq');
  const canManageSla = usePermission('manage_sla_settings');
  useChronicBusinessAlerts(!!user && canAnalytics);
  const isMobile = useIsMobile();
  const isChatPage = location.pathname === '/chat';
  const hideAppSidebarOnMobile = isMobile && isChatPage;
  const { Search } = Input;
  
  // Set page title
  useEffect(() => {
    document.title = 'Vitreous Support - Support Ticket Platform';
  }, []);

  // Get search query from URL if on search page
  const [searchQuery, setSearchQuery] = useState('');
  
  // Update search query when URL changes
  useEffect(() => {
    if (location.pathname === '/search') {
      const searchParams = new URLSearchParams(location.search);
      const query = searchParams.get('q') || '';
      setSearchQuery(query);
    } else {
      setSearchQuery('');
    }
  }, [location.pathname, location.search]);

  // Fetch version info with cache busting using buildNumber
  const { data: versionInfo } = useQuery(
    ['version'],
    async () => {
      const res = await api.get('/api/version', {
        params: { _cb: Date.now() } // Cache buster
      });
      return res.data;
    },
    { 
      staleTime: 1 * 60 * 1000, // 1 minute - allows version to update quickly
      refetchOnWindowFocus: true, // Refetch when window regains focus
      refetchOnMount: true, // Refetch when component mounts
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
      cacheTime: 2 * 60 * 1000 // Keep in cache for 2 minutes
    }
  );

  // Temporarily disable ticket counts query to prevent refresh loops
  // const { data: ticketCounts } = useQuery(
  //   ['ticket-counts'],
  //   async () => {
  //     const res = await api.get('/api/tickets/counts');
  //     return res.data;
  //   },
  //   { staleTime: 30_000, refetchInterval: 60_000 }
  // );
  const ticketCounts = { all: 0, open: 0, pending: 0, solved: 0, mine: 0, unassigned: 0 };

  const myTicketsNavLabel =
    user?.role === 'agent_partner' || user?.role === 'sales_rep_associated'
      ? 'Assigned to me'
      : 'My Tickets';

  const allTicketsNavLabel =
    user?.role === 'agent_partner' || user?.role === 'sales_rep_associated'
      ? 'Company tickets'
      : 'All Tickets';

  const isAgentOrSales = [
    'admin',
    'agent',
    'developer',
    'sales',
    'sales_agent',
    'sales_rep_internal',
    'agent_partner',
    'sales_rep_associated',
    'tv_dashboard',
  ].includes(user?.role || '');
  const [liveCallMeCount, setLiveCallMeCount] = useState<number>(0);
  const { data: callMeCountData } = useQuery(
    'call-me-count',
    () => api.get('/api/call-me/count').then((r) => r.data),
    { enabled: !!user && !!isAgentOrSales && canCallMeQueue, retry: false, refetchInterval: 10000, refetchIntervalInBackground: true }
  );
  useEffect(() => {
    if (!user || !isAgentOrSales || !canCallMeQueue) {
      setLiveCallMeCount(0);
      return;
    }

    let alive = true;
    const poll = async () => {
      try {
        const res = await api.get('/api/call-me/count', { params: { _ts: Date.now() } });
        if (alive) setLiveCallMeCount(Number(res.data?.count ?? 0));
      } catch {
        // keep last known count on transient errors
      }
    };

    poll();
    const id = setInterval(poll, 5000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [user, isAgentOrSales, canCallMeQueue]);

  const callMeCount = liveCallMeCount ?? callMeCountData?.count ?? 0;

  // Sidebar logo: company logo when available, otherwise Vitreous Support default
  const [companyLogoFailed, setCompanyLogoFailed] = useState(false);
  const [defaultLogoFailed, setDefaultLogoFailed] = useState(false);
  const basePath = process.env.PUBLIC_URL || '';
  const logoCacheBuster = versionInfo?.buildNumber ?? '1';
  const defaultLogoUrl = `${basePath}/logo.png?v=${logoCacheBuster}`.replace('//', '/');
  const companyLogoUrl = user?.company_logo_url || null;

  useEffect(() => {
    setCompanyLogoFailed(false);
    setDefaultLogoFailed(false);
  }, [user?.company_logo_url]);

  const useCompanyLogo = Boolean(companyLogoUrl && !companyLogoFailed);
  const sidebarLogoUrl = useCompanyLogo ? companyLogoUrl! : defaultLogoUrl;
  const logoAlt = useCompanyLogo ? 'Company Logo' : 'Vitreous Support Logo';
  const showLogoImage = Boolean(sidebarLogoUrl && !(sidebarLogoUrl === defaultLogoUrl && defaultLogoFailed));

  const handleLogoError = () => {
    if (useCompanyLogo) {
      setCompanyLogoFailed(true);
    } else {
      setDefaultLogoFailed(true);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGlobalSearch = (value: string) => {
    if (value.trim()) {
      navigate(`/search?q=${encodeURIComponent(value.trim())}`);
      // Don't clear searchQuery - let useEffect handle it from URL
    }
  };

  // Inject Jotform Agent script after login (not on login page)
  useEffect(() => {
    // Only inject if user is logged in and not on login/reset password/chat pages (chat has its own layout)
    if (!user || location.pathname === '/login' || location.pathname === '/reset-password' || location.pathname === '/chat') {
      // Remove script if user is not logged in or on login page
      const existing = document.querySelector('script[data-jf-agent="true"]') as HTMLScriptElement | null;
      if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing);
        // Also remove any Jotform iframe/widgets that might have been created
        const jotformWidgets = document.querySelectorAll('[id^="jf-"], [class^="jf-"]');
        jotformWidgets.forEach(widget => {
          if (widget.parentNode) {
            widget.parentNode.removeChild(widget);
          }
        });
      }
      return;
    }

    // Check if script already exists
    const existing = document.querySelector('script[data-jf-agent="true"]') as HTMLScriptElement | null;
    if (existing) return;

    // Inject Jotform Agent script
    const s = document.createElement('script');
    s.src = 'https://cdn.jotfor.ms/agent/embedjs/01973cd0ee467fc6a0f9dd4559fb817a84df/embed.js';
    s.async = true;
    s.defer = true;
    s.setAttribute('data-jf-agent', 'true');
    document.body.appendChild(s);

    // Cleanup function
    return () => {
      const el = document.querySelector('script[data-jf-agent="true"]') as HTMLScriptElement | null;
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
      // Also remove any Jotform widgets
      const jotformWidgets = document.querySelectorAll('[id^="jf-"], [class^="jf-"]');
      jotformWidgets.forEach(widget => {
        if (widget.parentNode) {
          widget.parentNode.removeChild(widget);
        }
      });
    };
  }, [user, location.pathname]);

  // Profile dropdown menu
  const profileMenu: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserSwitchOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
      danger: true,
    },
  ];

  // Navigation menu items
  const menuItems: MenuProps['items'] = [
    // Always include these items for all users
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => navigate('/dashboard'),
    },
    {
      key: '/tickets',
      icon: <FileTextOutlined />,
      label: 'Tickets',
      // Do NOT navigate on parent when children exist; rely on submenu items
      children: [
        {
          key: '/tickets?tab=all',
          label: (
            <span>
              {allTicketsNavLabel}
              {typeof ticketCounts?.all === 'number' && (
                <Badge count={ticketCounts.all} style={{ marginLeft: 8 }} />
              )}
            </span>
          ),
          onClick: () => navigate('/tickets?tab=all'),
        },
        ...(canCallMeQueue
          ? [
              {
                key: '/tickets?tab=callme',
                label: (
                  <span>
                    Call Me
                    {callMeCount > 0 && (
                      <Badge count={callMeCount} style={{ marginLeft: 8, backgroundColor: '#faad14' }} />
                    )}
                  </span>
                ),
                onClick: () => navigate('/tickets?tab=callme'),
              },
            ]
          : []),
        {
          key: '/tickets?tab=my',
          label: (
            <span>
              {myTicketsNavLabel}
              {typeof ticketCounts?.mine === 'number' && (
                <Badge count={ticketCounts.mine} style={{ marginLeft: 8 }} />
              )}
            </span>
          ),
          onClick: () => navigate('/tickets?tab=my'),
        },
        {
          key: '/tickets?tab=unassigned',
          label: (
            <span>
              Unassigned
              {typeof ticketCounts?.unassigned === 'number' && (
                <Badge count={ticketCounts.unassigned} style={{ marginLeft: 8 }} />
              )}
            </span>
          ),
          onClick: () => navigate('/tickets?tab=unassigned'),
        },
      ],
    },
    ...((user?.role === 'admin' || canManageReplacements)
      ? [
          {
            key: '/replacements',
            icon: <ToolOutlined />,
            label: 'Replacement',
            onClick: () => navigate('/equipment?tab=dashboard'),
          },
        ]
      : []),
    ...((user?.role === 'admin' || canManageCreditCardServices)
      ? [
          {
            key: '/credit-card-services',
            icon: <CreditCardOutlined />,
            label: 'Credit Card Services',
            onClick: () => navigate('/credit-card-services'),
          },
        ]
      : []),
    {
      key: '/businesses',
      icon: <ShopOutlined />,
      label: 'Businesses',
      onClick: () => navigate('/businesses'),
    },
    ...(canRetainIQ
      ? [
          {
            key: '/retain-iq',
            icon: <CustomerServiceOutlined />,
            label: 'RetainIQ',
            onClick: () => navigate('/retain-iq'),
          },
        ]
      : []),
    ...(user?.role === 'sales' || user?.role === 'sales_rep_internal'
      ? [
          {
            key: '/salesportal',
            icon: <FolderOutlined />,
            label: 'Sales Portal',
            onClick: () => navigate('/salesportal'),
          },
        ]
      : []),
    ...(user?.role === 'admin' || canManageSla
      ? [
          ...(user?.role === 'admin'
            ? [
                {
                  key: '/users',
                  icon: <UserOutlined />,
                  label: 'Users',
                  onClick: () => navigate('/users'),
                },
              ]
            : []),
        ]
      : []),
    ...((user?.role === 'admin' || canSalesPortalMgmt)
      ? [
          {
            key: '/salesportal-admin',
            icon: <FolderOutlined />,
            label: 'Sales Agent Portal',
            onClick: () => navigate('/salesportal'),
          },
        ]
      : []),
    ...((user?.role === 'admin' || canAnalytics)
      ? [
          {
            key: '/analytics',
            icon: <BarChartOutlined />,
            label: 'Analytics',
            onClick: () => navigate('/analytics'),
          },
          {
            key: '/problems',
            icon: <AlertOutlined />,
            label: 'Problems',
            onClick: () => navigate('/problems'),
          },
        ]
      : []),
    ...((user?.role === 'admin' || canFeedback)
      ? [
          {
            key: '/feedback-dashboard',
            icon: <StarOutlined />,
            label: 'Feedback',
            onClick: () => navigate('/feedback-dashboard'),
          },
        ]
      : []),
    ...((user?.role === 'admin' || canAuditLogs)
      ? [
          {
            key: '/audit-logs',
            icon: <FileTextOutlined />,
            label: 'Audit Logs',
            onClick: () => navigate('/audit-logs'),
          },
        ]
      : []),
    ...(user?.role === 'admin' || canManageSla
      ? [
          {
            key: '/settings',
            icon: <SettingOutlined />,
            label: 'Settings',
            onClick: () => navigate('/settings'),
          },
        ]
      : []),
    ...((user?.role === 'admin' || canTicketAudit)
      ? [
          {
            key: '/ticket-audit',
            icon: <FileSearchOutlined />,
            label: 'Ticket Audit',
            onClick: () => navigate('/ticket-audit'),
          },
        ]
      : []),
    // Call Center, Calls, and Remote Access hidden
  ];
  
  // Get selected menu key based on current path
  const getSelectedKey = () => {
    const path = location.pathname;
    const search = location.search;
    
    // Check if we're on a tickets page with a view parameter
    if (path === '/tickets' && search) {
      return `/tickets${search}`;
    }

    if (path === '/equipment') {
      const params = new URLSearchParams(search);
      const tab = params.get('tab');
      if (tab === 'replacements' || tab === 'dashboard' || !tab) {
        return '/replacements';
      }
    }

    if (path === '/salesportal' && (user?.role === 'admin' || canSalesPortalMgmt)) {
      return '/salesportal-admin';
    }

    return path;
  };

  // Get open submenu keys
  const getOpenKeys = () => {
    if (location.pathname.startsWith('/tickets')) {
      return ['/tickets'];
    }
    return [];
  };

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      {!hideAppSidebarOnMobile && (
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
        trigger={
          versionInfo ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                width: '100%',
                padding: '8px 0',
                color: 'rgba(255, 255, 255, 0.65)',
              }}
            >
              {!collapsed && (
                <Text style={{ fontSize: 11, color: 'rgba(255, 255, 255, 0.45)', textAlign: 'center' }}>
                  v{versionInfo.version}
                </Text>
              )}
              {collapsed ? (
                <RightOutlined style={{ fontSize: 12 }} />
              ) : (
                <LeftOutlined style={{ fontSize: 12 }} />
              )}
            </div>
          ) : undefined
        }
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        {/* Logo */}
        <div
          style={{
            minHeight: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px 16px',
            background: 'rgba(255, 255, 255, 0.05)',
          }}
        >
          {!collapsed ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 180, height: 100, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, overflow: 'hidden' }}>
                {showLogoImage ? (
                  <img 
                    key={sidebarLogoUrl}
                    src={sidebarLogoUrl} 
                    alt={logoAlt} 
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                    onError={handleLogoError}
                  />
                ) : (
                  <div style={{ fontSize: 48, fontWeight: 'bold', color: '#1976d2' }}>VS</div>
                )}
              </div>
            </div>
          ) : (
            <div
              style={{
                width: 64,
                height: 64,
                display: 'flex',
                background: '#fff',
                borderRadius: 8,
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {showLogoImage ? (
                <img 
                  key={sidebarLogoUrl}
                  src={sidebarLogoUrl} 
                  alt={logoAlt} 
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                  onError={handleLogoError}
                />
              ) : (
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1976d2' }}>VS</div>
              )}
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          defaultOpenKeys={getOpenKeys()}
          items={menuItems}
        />

      </Sider>
      )}

      <AntLayout style={{ marginLeft: hideAppSidebarOnMobile ? 0 : (collapsed ? 80 : 200), transition: 'all 0.2s' }}>
        {/* Header */}
        <Header
          style={{
            padding: hideAppSidebarOnMobile ? '0 12px' : '0 24px',
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 1px 4px rgba(0,21,41,.08)',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            gap: 0,
            height: 'auto',
            minHeight: 64,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, minHeight: 64 }}>
            <div>
              {!hideAppSidebarOnMobile && (collapsed ? (
                <RightOutlined
                  className="trigger"
                  onClick={() => setCollapsed(!collapsed)}
                  style={{ fontSize: 16, cursor: 'pointer' }}
                />
              ) : (
                <LeftOutlined
                  className="trigger"
                  onClick={() => setCollapsed(!collapsed)}
                  style={{ fontSize: 16, cursor: 'pointer' }}
                />
              ))}
              {hideAppSidebarOnMobile && (
                <span style={{ fontSize: 16, fontWeight: 600, color: '#1976d2' }}>Chat</span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, justifyContent: 'center', minWidth: 0 }}>
              <Search
                placeholder="Search tickets, businesses, users..."
                enterButton={<SearchOutlined />}
                size="middle"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onSearch={handleGlobalSearch}
                style={{ flex: 1, maxWidth: 400, minWidth: 200 }}
                allowClear
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {(user?.role === 'admin' || canAccessChat || ['agent', 'developer', 'sales_rep_internal'].includes(user?.role || '')) && (
              <Button
                type="default"
                icon={<MessageOutlined />}
                onClick={() => window.open('/chat', '_blank', 'noopener,noreferrer')}
              >
                Chat
              </Button>
            )}
            {!hideAppSidebarOnMobile && <NotificationBell user={user} />}
            <Dropdown menu={{ items: profileMenu }} placement="bottomRight">
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text strong>
                  {user?.first_name} {user?.last_name}
                </Text>
                <Avatar src={user?.avatar_url} style={{ backgroundColor: '#1976d2' }}>
                  {user?.first_name?.[0]}
                  {user?.last_name?.[0]}
                </Avatar>
              </div>
            </Dropdown>
            </div>
          </div>

          {isAgentOrSales && (
            <div style={{ borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #eceff3', padding: hideAppSidebarOnMobile ? '8px 12px' : '8px 24px', margin: hideAppSidebarOnMobile ? '0 -12px' : '0 -24px', display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, backgroundColor: '#fff' }}>
              <Button
                type="default"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setNewTicketModalOpen(true)}
              >
                New Ticket
              </Button>
              <WorkspaceTabBar compact />
            </div>
          )}
        </Header>

        <CreateTicketFromChatModal
          open={newTicketModalOpen}
          onClose={() => setNewTicketModalOpen(false)}
          title="New Ticket"
          okText="Create Ticket"
          wrapClassName="workspace-new-ticket-modal"
          onSuccess={(ticketNumber) => {
            if (ticketNumber) {
              tabsContext?.addTab(`number/${ticketNumber}`, `#${ticketNumber}`);
              navigate(`/workspace/number/${ticketNumber}`);
            } else {
              navigate('/tickets');
            }
          }}
        />

        {/* Content - no outer scroll on chat page so conversation list stays fixed */}
        <Content
          style={{
            margin: 0,
            padding: '56px 24px 24px 24px',
            minHeight: 'calc(100vh - 64px)',
            height: location.pathname === '/chat' ? 'calc(100vh - 64px)' : undefined,
            overflow: location.pathname === '/chat' ? 'hidden' : 'auto',
            background: '#fff',
          }}
        >
          {children}
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default LayoutComponent;
